// ./src/components/features/DisplayActivationFormModal/DisplayActivationFormModal.tsx
import React, { useState, useEffect } from "react";
import { TextInput, Textarea, Stack, Text, Grid, Alert } from "@mantine/core";

import { parseUserAgent } from "@/utils";
import { DisplayActivation, DisplayActivationInput } from "@/utils/api";

import { AppModal, AppIcon } from "@/components/generic";
import { DisplaySelector } from "@/components/features";

export interface DisplayActivationFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Display activation to edit (if null/undefined, it's create mode) */
  displayActivation?: DisplayActivation | null;

  /** Callback when display activation should be updated */
  onUpdateDisplayActivation?: (
    activationId: string,
    activationData: DisplayActivationInput,
  ) => Promise<DisplayActivation | null>;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from display activation operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface DisplayActivationFormData {
  displayId: string | null;
  name: string | null;
  location: string;
  brand: string;
  model: string;
  serialNumber: string;
  specs: string;
}

const initialFormData: DisplayActivationFormData = {
  displayId: null,
  name: null,
  location: "",
  brand: "",
  model: "",
  serialNumber: "",
  specs: "",
};

export const DisplayActivationFormModal: React.FC<
  DisplayActivationFormModalProps
> = ({
  opened,
  onClose,
  displayActivation,
  onUpdateDisplayActivation,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<DisplayActivationFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode =
    Boolean(displayActivation) && Boolean(displayActivation?.id);
  const isSubmitting = isUpdating;

  // Reset form when modal opens/closes or displayActivation changes
  useEffect(() => {
    if (opened) {
      if (displayActivation) {
        // Edit mode - populate form with display activation data
        setFormData({
          displayId: displayActivation.displayId || null,
          name: displayActivation.name || null,
          location: displayActivation.location || "",
          brand: displayActivation.brand || "",
          model: displayActivation.model || "",
          serialNumber: displayActivation.serialNumber || "",
          specs: displayActivation.specs || "",
        });
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, displayActivation, onClearError]);

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.displayId) {
      newErrors.displayId = "Display selection is required";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof DisplayActivationFormData,
    value: string | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof DisplayActivationFormData,
  ): string | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare display activation data
      // Note: We do NOT include userAgent, platform, or resolution
      // as per requirements - these should not be changed by the user
      const activationData: DisplayActivationInput = {
        displayId: formData.displayId,
        name: formData.name,
        location: formData.location.trim() || null,
        brand: formData.brand.trim() || null,
        model: formData.model.trim() || null,
        serialNumber: formData.serialNumber.trim() || null,
        specs: formData.specs.trim() || null,
      };

      let result: DisplayActivation | null = null;

      if (isEditMode && displayActivation && onUpdateDisplayActivation) {
        // Update existing display activation
        result = await onUpdateDisplayActivation(
          displayActivation.id || "",
          activationData,
        );
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors at the bottom of the form
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title="Edit Screen Details"
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Save Changes",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Name"
              placeholder="The screen name"
              value={formData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={getDisplayError("name")}
              size="md"
            />
          </Grid.Col>
        </Grid>
        {/* Display Selection */}
        <Grid>
          <Grid.Col span={12}>
            <DisplaySelector
              label="Display"
              placeholder="Select a display"
              value={formData.displayId}
              onChange={(displayId) =>
                handleInputChange("displayId", displayId as string | null)
              }
              error={getDisplayError("displayId")}
              required
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Device Information */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Brand"
              placeholder="e.g., Samsung, LG, Sony"
              value={formData.brand}
              onChange={(e) => handleInputChange("brand", e.target.value)}
              error={getDisplayError("brand")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Model"
              placeholder="e.g., UN55TU7000"
              value={formData.model}
              onChange={(e) => handleInputChange("model", e.target.value)}
              error={getDisplayError("model")}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Serial Number and Location */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Serial Number"
              placeholder="Enter serial number"
              value={formData.serialNumber}
              onChange={(e) =>
                handleInputChange("serialNumber", e.target.value)
              }
              error={getDisplayError("serialNumber")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Location"
              placeholder="e.g., Lobby, Room 101"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              error={getDisplayError("location")}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Technical Specifications */}
        <Grid>
          <Grid.Col span={12}>
            <Textarea
              label="Technical Specifications"
              placeholder="Enter detailed specifications (e.g., screen size, resolution capabilities, connectivity options)"
              value={formData.specs}
              onChange={(e) => handleInputChange("specs", e.target.value)}
              error={getDisplayError("specs")}
              rows={4}
              autosize
              minRows={3}
              maxRows={8}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Read-only System Information */}
        {displayActivation && (
          <Grid>
            <Grid.Col span={12}>
              <Alert
                color="blue"
                icon={<AppIcon iconName="InfoCircle" size={16} />}
                title="System Information (Read-only)"
              >
                <Stack gap="xs">
                  {displayActivation.userAgent &&
                    (() => {
                      const { browser, os, device } = parseUserAgent(
                        displayActivation.userAgent,
                      );
                      return (
                        <>
                          <Text size="sm">
                            <Text component="span" fw={600}>
                              Browser:
                            </Text>{" "}
                            {browser}
                          </Text>
                          <Text size="sm">
                            <Text component="span" fw={600}>
                              Operating System:
                            </Text>{" "}
                            {os}
                          </Text>
                          <Text size="sm">
                            <Text component="span" fw={600}>
                              Device:
                            </Text>{" "}
                            {device}
                          </Text>
                        </>
                      );
                    })()}
                  {displayActivation.resolution && (
                    <Text size="sm">
                      <Text component="span" fw={600}>
                        Resolution:
                      </Text>{" "}
                      {displayActivation.resolution}
                    </Text>
                  )}
                  {displayActivation.ipAddress && (
                    <Text size="sm">
                      <Text component="span" fw={600}>
                        IP Address:
                      </Text>{" "}
                      {displayActivation.ipAddress}
                    </Text>
                  )}
                </Stack>
              </Alert>
            </Grid.Col>
          </Grid>
        )}

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
